import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api from '../lib/api';
import { useBillingStore, type SubscriptionStatus } from './billingStore';
import { recordSyncSample } from '../lib/observability';

export interface QueuedTransaction {
  id: string; // local UUID
  tankId: string;
  type: 'INBOUND' | 'OUTBOUND';
  fuelType?: string;
  volumeL: number;
  pricePerL?: number;
  notes?: string;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
  error?: string;
  timestamp: number;
}

function normalizeQueuedTransaction(transaction: QueuedTransaction): QueuedTransaction {
  return {
    ...transaction,
    status: transaction.status === 'syncing' ? 'pending' : transaction.status,
  };
}

interface OfflineQueueState {
  queue: QueuedTransaction[];
  isOnline: boolean;
  isSyncing: boolean;

  // Actions
  addToQueue: (transaction: Omit<QueuedTransaction, 'id' | 'status' | 'timestamp'>) => void;
  removeFromQueue: (id: string) => void;
  syncQueue: () => Promise<void>;
  clearFailed: () => void;
  setOnline: (online: boolean) => void;
}

const OFFLINE_QUEUE_LOCKED_SUBSCRIPTION_STATUSES = new Set<SubscriptionStatus>([
  'trial_expired',
  'past_due',
  'cancelled',
]);
const OFFLINE_QUEUE_READ_ONLY_ERROR =
  'This company is read-only. Queued transactions are preserved but cannot sync until billing is restored.';

function isOfflineQueueWriteLocked(): boolean {
  return OFFLINE_QUEUE_LOCKED_SUBSCRIPTION_STATUSES.has(useBillingStore.getState().subscriptionStatus);
}

export const useOfflineQueueStore = create<OfflineQueueState>()(
  persist(
    (set, get) => ({
      queue: [],
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      isSyncing: false,

      addToQueue: (transaction) => {
        const id = crypto.randomUUID();
        const queued: QueuedTransaction = {
          ...transaction,
          id,
          status: 'pending',
          timestamp: Date.now(),
        };
        set((state) => ({ queue: [...state.queue, queued] }));

        // Auto-sync if online
        if (get().isOnline) {
          get().syncQueue();
        }
      },

      removeFromQueue: (id) => {
        set((state) => ({
          queue: state.queue.filter((t) => t.id !== id),
        }));
      },

      syncQueue: async () => {
        const state = get();
        if (state.isSyncing || !state.isOnline || state.queue.length === 0) return;
        if (isOfflineQueueWriteLocked()) {
          set((current) => ({
            queue: current.queue.map((transaction) =>
              transaction.status === 'pending' || transaction.status === 'syncing'
                ? {
                    ...transaction,
                    status: 'failed',
                    error: OFFLINE_QUEUE_READ_ONLY_ERROR,
                  }
                : transaction,
            ),
            isSyncing: false,
          }));
          return;
        }

        set({ isSyncing: true });
        const queue = state.queue.filter((t) => t.status !== 'synced');

        for (const transaction of queue) {
          try {
            set((state) => ({
              queue: state.queue.map((t) =>
                t.id === transaction.id ? { ...t, status: 'syncing' as const } : t,
              ),
            }));

            await api.post('/transactions', {
              tankId: transaction.tankId,
              type: transaction.type,
              fuelType: transaction.fuelType,
              volumeL: transaction.volumeL,
              pricePerL: transaction.pricePerL,
              notes: transaction.notes,
            });

            recordSyncSample(true);

            set((state) => ({
              queue: state.queue.map((t) =>
                t.id === transaction.id ? { ...t, status: 'synced' as const } : t,
              ),
            }));
          } catch (error) {
            recordSyncSample(false);
            set((state) => ({
              queue: state.queue.map((t) =>
                t.id === transaction.id
                  ? { ...t, status: 'failed' as const, error: (error as any).message }
                  : t,
              ),
            }));
          }
        }

        set({ isSyncing: false });
      },

      clearFailed: () => {
        set((state) => ({
          queue: state.queue.filter((t) => t.status !== 'failed'),
        }));
      },

      setOnline: (online) => {
        set({ isOnline: online });
        if (online) {
          get().syncQueue();
        }
      },
    }),
    {
      name: 'offline-queue',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        queue: state.queue,
      }),
      merge: (persistedState, currentState) => {
        const typedState = (persistedState as Partial<OfflineQueueState>) ?? {};
        return {
          ...currentState,
          ...typedState,
          queue: Array.isArray(typedState.queue)
            ? typedState.queue.map((transaction) => normalizeQueuedTransaction(transaction as QueuedTransaction))
            : currentState.queue,
        };
      },
      onRehydrateStorage: () => (state) => {
        if (!state || typeof window === 'undefined') return;
        const online = navigator.onLine;
        state.setOnline(online);
        if (online && state.queue.some((transaction) => transaction.status !== 'synced')) {
          window.setTimeout(() => void state.syncQueue(), 0);
        }
      },
    },
  ),
);

// Listen to online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => useOfflineQueueStore.getState().setOnline(true));
  window.addEventListener('offline', () => useOfflineQueueStore.getState().setOnline(false));
}
